import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { applyInvalidations, INVALIDATES } from "../api/invalidates";
import { merchantFlowService } from "../api/merchantFlowService";
import type { FlowDetail } from "../types";

/**
 * What invalidating does beyond killing the link. A flow with a contract step
 * sends out what was signed (the stop's bundle); one without has nothing to
 * sign, and the files its merchant uploaded stay linked to the deal.
 */
export function invalidateConsequence(flow: Pick<FlowDetail, "steps">): string {
  return flow.steps.some((step) => step.type === "contract")
    ? "The link stops working, and anything already signed is sent out."
    : "The link stops working. Files the merchant already uploaded stay on the deal.";
}

/**
 * Resend and invalidate for one flow, as FlowDetailModal and the assistant's sent-flow card offer
 * them: the tap is the action, and invalidating takes a second tap (`confirmingInvalidate`).
 */
export function useFlowActions(borrowerId: number, flowId: number) {
  const queryClient = useQueryClient();
  const [confirmingInvalidate, setConfirmingInvalidate] = useState(false);

  // Settled, not success: a 409 means the list was stale.
  const resend = useMutation({
    mutationFn: () => merchantFlowService.resend(borrowerId, flowId),
    onSettled: () => applyInvalidations(queryClient, INVALIDATES.resend(borrowerId)),
  });
  const invalidate = useMutation({
    mutationFn: () => merchantFlowService.invalidate(borrowerId, flowId),
    onSettled: () => {
      setConfirmingInvalidate(false);
      applyInvalidations(queryClient, INVALIDATES.invalidate(borrowerId, flowId));
    },
  });
  // Only the latest action's outcome is shown.
  const resendInvite = () => {
    invalidate.reset();
    resend.mutate();
  };
  const invalidateLink = () => {
    resend.reset();
    invalidate.mutate();
  };

  return {
    resend,
    invalidate,
    resendInvite,
    invalidateLink,
    confirmingInvalidate,
    setConfirmingInvalidate,
    failed: resend.error ?? invalidate.error,
    busy: resend.isPending || invalidate.isPending,
  };
}
